import textToSVG from 'text-to-svg';
import stringToHTML from '../stringToHTML';
import textToPath from './lib/textToPath';

const transformersCache = {};
const pendingTransformerSubscribers = {};

/**
 * Draws Given path in order to calculate the visual metrics
 * @param {Array} paths - The svg paths to measure
 */
const measurePathsMetrics = (paths) => {
    const svg = stringToHTML(
        `<svg xmlns="http://www.w3.org/2000/svg">${paths
            .map((path) => `<path transform="${path.transform ? path.transform : 'translate(0 0)'}" d="${path.d}"/>`)
            .join('')}</svg>`
    );

    document.body.appendChild(svg);

    const { height, width, x, y } = svg.getBBox();

    document.body.removeChild(svg);

    return {
        height,
        width,
        viewBox: `${x} ${y} ${width} ${height}`,
    };
};

/**
 * Drawable path data
 * @typedef {Object} DrawablePath
 * @property {String} id - Path renderable id
 * @property {String} d - Path d svg attribute, The actual path content
 * @property {String} transform - The path transform property
 */
export const SVGTransformer = {
    /**
     * Returns new TextToSVG Transformer / Cached one if exists
     * @param url - The font ttf file url
     * @returns {Promise<Object>}
     */
    loadTransformer: async (url) =>
        new Promise((resolve) => {
            if (!url) {
                throw new Error('Cannot load text-to-svg transformer with empty url');
            }

            if (transformersCache[url]) {
                return resolve(transformersCache[url]);
            }

            // Checking if requested transformer has pending subscriber's chain, If so, Adding this consumer as subscriber
            if (pendingTransformerSubscribers[url]) {
                const currentSubscribers = pendingTransformerSubscribers[url];
                pendingTransformerSubscribers[url] = [
                    ...currentSubscribers,
                    (fontTransformer) => resolve(fontTransformer),
                ];

                return;
            }

            // Setting as the initial transformer subscriber
            pendingTransformerSubscribers[url] = [(transformer) => resolve(transformer)];

            const httpsUrl = url.replace(/^http:\/\//i, 'https://');
            textToSVG.load(httpsUrl, (err, fontTransformer) => {
                if (err) {
                    throw err;
                }

                transformersCache[url] = fontTransformer;

                // Running transformer subscribers callback
                const subscriberCallbacks = pendingTransformerSubscribers[url];
                subscriberCallbacks.forEach((callback) => callback(fontTransformer));

                // Cleanup
                pendingTransformerSubscribers[url] = [];
                delete pendingTransformerSubscribers[url];
            });
        }),

    /**
     * Determines for a given url if it's transformer is active
     * @param {String} url - The url to check
     * @returns {Boolean}
     */
    isTransformerActive: (url) => !!transformersCache[url],

    /** Returns a drawable paths svg data for a given text using text-to-svg package
     * @see https://github.com/shrhdk/text-to-svg
     * @param {String} text - The text to transform into drawable path
     * @param {Object} fontUrl - The font tff file downloadable url
     * @param {String} fontSize - The size of the text
     * @param {String} letterSpacing - The spacing between the text letters
     * @param {String} curvatureAngle - The angel that creates the text curve
     * @returns {Promise<{paths: Array[DrawablePath], metrics: {height, width, viewBox}}>}
     */
    textToPath: async ({ text = '', fontUrl, fontSize, letterSpacing, curvatureAngle }) => {
        const transformer = await SVGTransformer.loadTransformer(fontUrl);

        const textPaths = textToPath(transformer, text, {
            fontSize,
            letterSpacing,
            curvatureAngle,
        });

        return {
            paths: textPaths,
            metrics: measurePathsMetrics(textPaths),
        };
    },
};
